/**
 * Alat bantu diagnosa error API.
 *
 * Tujuan: saat request gagal, user sering langsung ter-redirect ke halaman
 * login sehingga isi console ikut terhapus sebelum sempat dibaca. Modul ini:
 *
 *  1. Mencatat SETIAP error API ke console dengan detail lengkap.
 *  2. Menyimpan error terakhir ke localStorage supaya tetap bisa dibaca
 *     SETELAH halaman berpindah / user terlogout.
 *  3. Menyediakan mode debug yang MENAHAN logout & redirect otomatis, supaya
 *     error bisa diperiksa dengan tenang.
 *
 * Cara pakai di browser (tanpa perlu build ulang):
 *   speakDebug.on()      -> aktifkan mode debug (tidak akan auto-logout)
 *   speakDebug.last()    -> lihat error API terakhir
 *   speakDebug.errors()  -> lihat riwayat error API
 *   speakDebug.off()     -> kembali ke perilaku normal
 */

import { APP_VERSION } from '../constants/appVersion';

const DEBUG_KEY = 'speak:debug-api';
const ERROR_LOG_KEY = 'speak:api-errors';
const MAX_LOGGED_ERRORS = 15;
const MAX_TEXT_LENGTH = 4000;

const safeLocalStorage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* storage penuh / diblokir browser */
    }
  },
  remove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      /* abaikan */
    }
  },
};

/** Mode debug aktif bila di-set lewat console, atau lewat env saat build. */
export const isApiDebugEnabled = () => {
  if (process.env.REACT_APP_DEBUG_API === 'true') return true;
  return safeLocalStorage.get(DEBUG_KEY) === '1';
};

export const setApiDebug = (enabled) => {
  if (enabled) {
    safeLocalStorage.set(DEBUG_KEY, '1');
  } else {
    safeLocalStorage.remove(DEBUG_KEY);
  }
  return isApiDebugEnabled();
};

const truncate = (value) => {
  if (typeof value !== 'string') return value;
  return value.length > MAX_TEXT_LENGTH
    ? `${value.slice(0, MAX_TEXT_LENGTH)}… [dipotong, total ${value.length} karakter]`
    : value;
};

/** Ringkas error axios menjadi objek datar yang aman disimpan & dibaca. */
export const describeApiError = (error, context = {}) => {
  const response = error?.response;
  const config = error?.config || {};

  let responseBody = response?.data;
  if (typeof responseBody === 'string') {
    responseBody = truncate(responseBody);
  } else if (responseBody && typeof responseBody === 'object') {
    try {
      responseBody = JSON.parse(truncate(JSON.stringify(responseBody)));
    } catch (e) {
      responseBody = '[tidak dapat diserialisasi]';
    }
  }

  // Header balasan sering menjadi satu-satunya petunjuk SIAPA yang menolak
  // request (nginx, ModSecurity, Cloudflare, F5, dsb).
  let responseHeaders = null;
  try {
    const raw = response?.headers;
    if (raw) {
      responseHeaders =
        typeof raw.toJSON === 'function' ? raw.toJSON() : { ...raw };
    }
  } catch (e) {
    responseHeaders = '[tidak dapat dibaca]';
  }

  return {
    waktu: new Date().toISOString(),
    versiAplikasi: APP_VERSION,
    halaman: typeof window !== 'undefined' ? window.location.pathname : null,
    method: (config.method || '').toUpperCase() || null,
    url: config.url || null,
    baseURL: config.baseURL || null,
    status: response?.status ?? null,
    statusText: response?.statusText ?? null,
    kodeError: response?.data?.code || error?.code || null,
    pesanServer: response?.data?.message ?? null,
    tipeBody: typeof response?.data,
    body: responseBody ?? null,
    headerBalasan: responseHeaders,
    ukuranBodyRequest: config?.data?.length ?? null,
    pesanAxios: error?.message || null,
    adaResponse: Boolean(response),
    // true bila balasan berasal dari aplikasi kita (JSON terstruktur),
    // false bila dari perangkat di depannya (nginx/WAF -> HTML atau kosong).
    dariAplikasi: Boolean(
      response &&
      response.data &&
      typeof response.data === 'object' &&
      (response.data.message || response.data.code)
    ),
    ...context,
  };
};

const readStoredErrors = () => {
  const raw = safeLocalStorage.get(ERROR_LOG_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

/**
 * Catat error ke console DAN ke localStorage.
 * Dipanggil sebelum redirect apa pun, supaya jejaknya tidak hilang.
 */
export const logApiError = (error, context = {}) => {
  const detail = describeApiError(error, context);

  /* eslint-disable no-console */
  console.error(
    `[SPEAK][API ERROR] ${detail.method || '?'} ${detail.url || '?'} -> ` +
    `${detail.status ?? 'tanpa balasan'} ${detail.kodeError ? `(${detail.kodeError})` : ''}`
  );
  console.error('[SPEAK][API ERROR] detail:', detail);
  console.error('[SPEAK][API ERROR] error asli:', error);
  /* eslint-enable no-console */

  const stored = [detail, ...readStoredErrors()].slice(0, MAX_LOGGED_ERRORS);
  safeLocalStorage.set(ERROR_LOG_KEY, JSON.stringify(stored));

  if (typeof window !== 'undefined') {
    window.__SPEAK_LAST_API_ERROR__ = detail;
  }

  return detail;
};

export const getLoggedApiErrors = () => readStoredErrors();
export const clearLoggedApiErrors = () => safeLocalStorage.remove(ERROR_LOG_KEY);

/**
 * Banner saat aplikasi dimuat — untuk memastikan build MANA yang sedang live.
 * Kalau baris ini tidak muncul di console, berarti browser/server masih
 * menyajikan bundle lama (perlu hard refresh atau clear cache CDN/nginx).
 */
export const printBootBanner = () => {
  /* eslint-disable no-console */
  console.info(
    `%c[SPEAK] v${APP_VERSION} · penanganan error API v2 aktif`,
    'background:#26D07C;color:#111;padding:2px 6px;border-radius:3px;font-weight:bold'
  );
  console.info(
    '[SPEAK] Ketik speakDebug.help() di console untuk alat bantu diagnosa error.'
  );
  if (isApiDebugEnabled()) {
    console.warn(
      '[SPEAK] MODE DEBUG API AKTIF — logout & redirect otomatis DITAHAN. ' +
      'Matikan dengan speakDebug.off() bila sudah selesai.'
    );
  }
  /* eslint-enable no-console */
};

/** Pasang helper ke window supaya bisa dipanggil langsung dari console. */
export const installDebugConsoleHelpers = () => {
  if (typeof window === 'undefined') return;

  window.speakDebug = {
    help() {
      /* eslint-disable no-console */
      console.info(
        [
          'Perintah yang tersedia:',
          '  speakDebug.on()      aktifkan mode debug (logout & redirect otomatis ditahan)',
          '  speakDebug.off()     kembali ke perilaku normal',
          '  speakDebug.status()  cek mode debug aktif atau tidak',
          '  speakDebug.last()    error API terakhir (tetap ada meski sudah pindah halaman)',
          '  speakDebug.errors()  riwayat error API terakhir',
          '  speakDebug.clear()   hapus riwayat error',
          '  speakDebug.version() versi aplikasi yang sedang berjalan',
        ].join('\n')
      );
      /* eslint-enable no-console */
    },
    on() {
      setApiDebug(true);
      /* eslint-disable-next-line no-console */
      console.warn(
        '[SPEAK] Mode debug AKTIF. Logout & redirect otomatis ditahan. ' +
        'Ulangi aksi yang error, lalu baca [SPEAK][API ERROR] di console.'
      );
      return true;
    },
    off() {
      setApiDebug(false);
      /* eslint-disable-next-line no-console */
      console.info('[SPEAK] Mode debug NONAKTIF.');
      return false;
    },
    status: () => isApiDebugEnabled(),
    last: () => window.__SPEAK_LAST_API_ERROR__ || readStoredErrors()[0] || null,
    errors: () => readStoredErrors(),
    clear: () => {
      clearLoggedApiErrors();
      window.__SPEAK_LAST_API_ERROR__ = undefined;
      return true;
    },
    version: () => APP_VERSION,
  };
};
